/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/**
 * Price-based plan tier helpers.
 *
 * Plan identity is the UUID `id` (the backend is ADDING `planId` everywhere —
 * `planName`/`chosenSubscriptionPlan` is NOT removed, it stays for display).
 * Tier ordering is derived from `price` (higher price = higher tier); there is
 * no plan-name logic here by design (no PLAN_TIER map, no `=== 'Introductory'`).
 *
 * Phase 1: this module is intentionally unused — wiring happens in later phases.
 */

export interface PlanLike {
  id: string
  name?: string
  price: number | string
  trialDays?: number
  signupOnly?: boolean
}

/** Numeric price, tolerant of the decimal-string form the API returns. */
export const planPrice = (p: Pick<PlanLike, 'price'>): number => Number(p.price) || 0

/**
 * Perpetual ("free forever") plan: no price and no trial. Active $0
 * subscription, never expires, no card required at signup.
 * Derived client-side — the backend does not expose an `isPerpetual` flag
 * because price + trialDays already encode it.
 */
export const isPerpetual = (
  p: Pick<PlanLike, 'price' | 'trialDays'>
): boolean => planPrice(p) === 0 && (p.trialDays ?? 0) === 0

/**
 * Free-trial plan: $0 price for the first N days, then expires unless a
 * payment method is added. Distinct from a perpetual plan even though both
 * carry `price: 0`.
 */
export const isFreeTrial = (
  p: Pick<PlanLike, 'price' | 'trialDays'>
): boolean => planPrice(p) === 0 && (p.trialDays ?? 0) > 0

/**
 * How long access lasts after a ONE_TIME purchase, formatted for display.
 * The backend stores `accessDurationMonths`: null/0 = lifetime, 12 = 12 months.
 * Only meaningful for ONE_TIME plans (SUBSCRIPTION plans renew, so the field is
 * ignored there). Returns "Lifetime" when null/0, otherwise "N month(s)".
 */
export const formatAccessDuration = (
  months: number | null | undefined
): string => {
  if (months === null || months === undefined || months <= 0) return 'Lifetime'
  return months === 1 ? '1 month' : `${months} months`
}

/**
 * `<option>` label for a plan picker: "Name — $9/month", "Name — Free", or just
 * "Name" when no price is set. Single source for the signup plan dropdowns.
 */
export const formatPlanOptionLabel = (
  plan: { name?: string; price?: number | string | null; billingInterval?: string }
): string => {
  if (plan.price == null) return plan.name ?? ''
  const amount = Number(plan.price) === 0 ? 'Free' : `$${plan.price}`
  const interval = plan.billingInterval ? `/${plan.billingInterval}` : ''
  return `${plan.name} — ${amount}${interval}`
}

/** Resolve a plan from a list by its UUID. Returns null when not found. */
export const findPlanById = <T extends {id: string}>(
  plans: T[],
  planId: string | null | undefined
): T | null => (planId ? plans.find((p) => p.id === planId) ?? null : null)

/** A target plan is a downgrade when it costs less than the current plan. */
export const isDowngrade = (
  target: Pick<PlanLike, 'price'>,
  current: Pick<PlanLike, 'price'> | null | undefined
): boolean => !!current && planPrice(target) < planPrice(current)

/**
 * Change-plan UI rule (spec #4): a plan is selectable as a *change* only if it
 * is not signup-only, not the current plan, and not a downgrade. The backend
 * still enforces this (400) — this is just to disable the option client-side.
 */
export const isSelectableAsChange = (
  target: PlanLike,
  current: PlanLike | null | undefined
): boolean => {
  if (target.signupOnly) return false
  if (current && target.id === current.id) return false
  return !isDowngrade(target, current)
}
